import {
  advanceHintEscalation,
  createHintEscalationState,
  createRetryFeedback,
  type ActiveHintStage,
  type HintEscalationState,
  type RetryFeedback
} from "@/game/feedback";
import type { InputMethod } from "@/game/input";

import {
  getOrderedInstructionSteps,
  type InstructionPayload,
  type InstructionStep
} from "./schema";

export type InstructionSessionStatus = "active" | "completed";

export interface InstructionResponse {
  readonly action: string;
  readonly targetId: string;
  readonly inputMethod: InputMethod;
}

export interface GentleInstructionHint {
  readonly stage: "gentle-hint";
  readonly stepIndex: number;
  readonly action: string;
}

export interface ReducedChoicesInstructionHint {
  readonly stage: "reduced-choices";
  readonly stepIndex: number;
  readonly actions: readonly string[];
  readonly targetIds: readonly string[];
}

export interface ReplayInstructionHint {
  readonly stage: "replay-demonstration";
  readonly stepIndex: number;
  readonly steps: readonly InstructionStep[];
}

export type InstructionHint =
  | GentleInstructionHint
  | ReducedChoicesInstructionHint
  | ReplayInstructionHint;

export type InstructionMismatch =
  | "action"
  | "target"
  | "action-and-target";

export type InstructionTransition =
  | {
      readonly kind: "accepted";
      readonly stepIndex: number;
      readonly response: InstructionResponse;
      readonly completed: boolean;
    }
  | {
      readonly kind: "retry";
      readonly stepIndex: number;
      readonly mismatch: InstructionMismatch;
      readonly feedback: RetryFeedback;
      readonly hint: InstructionHint;
    }
  | {
      readonly kind: "hint";
      readonly hint: InstructionHint;
    }
  | {
      readonly kind: "undone";
      readonly removedResponse: InstructionResponse;
      readonly nextStepIndex: number;
    }
  | {
      readonly kind: "instruction-replayed";
      readonly steps: readonly InstructionStep[];
      readonly replayCount: number;
    };

export interface InstructionSessionState {
  readonly status: InstructionSessionStatus;
  readonly responses: readonly InstructionResponse[];
  readonly acceptedActionCount: number;
  readonly retryCount: number;
  readonly undoCount: number;
  readonly replayCount: number;
  readonly hintState: HintEscalationState;
  readonly lastTransition: InstructionTransition | null;
}

export type InstructionSessionAction =
  | {
      readonly type: "perform-step";
      readonly action: string;
      readonly targetId: string;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "undo" }
  | { readonly type: "request-hint" }
  | { readonly type: "replay-instruction" }
  | { readonly type: "restart" };

interface AdvancedInstructionHint {
  readonly hintState: HintEscalationState;
  readonly hint: InstructionHint;
  readonly replayCount: number;
}

export function createInstructionSessionState(): InstructionSessionState {
  return {
    status: "active",
    responses: [],
    acceptedActionCount: 0,
    retryCount: 0,
    undoCount: 0,
    replayCount: 0,
    hintState: createHintEscalationState(),
    lastTransition: null
  };
}

export function getNextInstructionStep(
  payload: InstructionPayload,
  state: InstructionSessionState
): InstructionStep | undefined {
  return getOrderedInstructionSteps(payload)[state.responses.length];
}

export function canUndoInstruction(state: InstructionSessionState): boolean {
  return state.responses.length > 0;
}

function getReducedChoiceIds(
  orderedIds: readonly string[],
  expectedId: string
): readonly string[] {
  const distractorId = orderedIds.find((id) => id !== expectedId);

  return orderedIds.filter(
    (id) => id === expectedId || id === distractorId
  );
}

function getOrderedInstructionActions(
  payload: InstructionPayload
): readonly string[] {
  const actions = getOrderedInstructionSteps(payload).map(
    (step) => step.action
  );

  return actions.filter(
    (action, index) => actions.indexOf(action) === index
  );
}

function createInstructionHint(
  payload: InstructionPayload,
  step: InstructionStep,
  stepIndex: number,
  stage: ActiveHintStage
): InstructionHint {
  switch (stage) {
    case "gentle-hint":
      return {
        stage,
        stepIndex,
        action: step.action
      };
    case "reduced-choices":
      return {
        stage,
        stepIndex,
        actions: getReducedChoiceIds(
          getOrderedInstructionActions(payload),
          step.action
        ),
        targetIds: getReducedChoiceIds(
          payload.targets.map(({ id }) => id),
          step.targetId
        )
      };
    case "replay-demonstration":
      return {
        stage,
        stepIndex,
        steps: getOrderedInstructionSteps(payload)
      };
  }
}

function advanceInstructionHint(
  payload: InstructionPayload,
  state: InstructionSessionState,
  step: InstructionStep
): AdvancedInstructionHint {
  const hintState = advanceHintEscalation(state.hintState);
  const hint = createInstructionHint(
    payload,
    step,
    state.responses.length,
    hintState.stage
  );

  return {
    hintState,
    hint,
    replayCount:
      state.replayCount + (hint.stage === "replay-demonstration" ? 1 : 0)
  };
}

function getInstructionMismatch(
  expectedStep: InstructionStep,
  action: Extract<InstructionSessionAction, { type: "perform-step" }>
): InstructionMismatch {
  const actionMatches = action.action === expectedStep.action;
  const targetMatches = action.targetId === expectedStep.targetId;

  if (!actionMatches && !targetMatches) {
    return "action-and-target";
  }

  return actionMatches ? "target" : "action";
}

export function reduceInstructionSession(
  state: InstructionSessionState,
  payload: InstructionPayload,
  action: InstructionSessionAction
): InstructionSessionState {
  switch (action.type) {
    case "perform-step": {
      if (state.status === "completed") {
        return state;
      }

      const expectedStep = getNextInstructionStep(payload, state);

      if (expectedStep === undefined) {
        return {
          ...state,
          status: "completed"
        };
      }

      const response: InstructionResponse = {
        action: action.action,
        targetId: action.targetId,
        inputMethod: action.inputMethod
      };
      const isCorrect =
        action.action === expectedStep.action &&
        action.targetId === expectedStep.targetId;

      if (!isCorrect) {
        const retryTransition = createRetryFeedback(state.hintState);
        const hint = createInstructionHint(
          payload,
          expectedStep,
          state.responses.length,
          retryTransition.feedback.hintStage
        );

        return {
          ...state,
          retryCount: state.retryCount + 1,
          replayCount:
            state.replayCount +
            (hint.stage === "replay-demonstration" ? 1 : 0),
          hintState: retryTransition.hintState,
          lastTransition: {
            kind: "retry",
            stepIndex: state.responses.length,
            mismatch: getInstructionMismatch(expectedStep, action),
            feedback: retryTransition.feedback,
            hint
          }
        };
      }

      const responses = [...state.responses, response];
      const completed = responses.length === payload.steps.length;

      return {
        ...state,
        status: completed ? "completed" : "active",
        responses,
        acceptedActionCount: state.acceptedActionCount + 1,
        lastTransition: {
          kind: "accepted",
          stepIndex: responses.length - 1,
          response,
          completed
        }
      };
    }
    case "undo": {
      const removedResponse = state.responses.at(-1);

      if (removedResponse === undefined) {
        return state;
      }

      const responses = state.responses.slice(0, -1);

      return {
        ...state,
        status: "active",
        responses,
        acceptedActionCount: Math.max(0, state.acceptedActionCount - 1),
        undoCount: state.undoCount + 1,
        lastTransition: {
          kind: "undone",
          removedResponse,
          nextStepIndex: responses.length
        }
      };
    }
    case "request-hint": {
      const expectedStep = getNextInstructionStep(payload, state);

      if (expectedStep === undefined) {
        return state;
      }

      const advancedHint = advanceInstructionHint(payload, state, expectedStep);

      return {
        ...state,
        hintState: advancedHint.hintState,
        replayCount: advancedHint.replayCount,
        lastTransition: {
          kind: "hint",
          hint: advancedHint.hint
        }
      };
    }
    case "replay-instruction": {
      const replayCount = state.replayCount + 1;

      return {
        ...state,
        replayCount,
        lastTransition: {
          kind: "instruction-replayed",
          steps: getOrderedInstructionSteps(payload),
          replayCount
        }
      };
    }
    case "restart":
      return createInstructionSessionState();
  }
}
